import React, { useState, useMemo, useRef, useEffect } from 'react';
import Select from 'react-select';
import { ChevronsUpDown, ChevronUp, ChevronDown, Filter, CheckCircle2, Download, X } from 'lucide-react';
import { useSelectStyles } from '../utils/selectStyles';

// Fonction pour obtenir la valeur brute d'une cellule, qu'elle utilise render ou non
const getRawCellValue = (item, column) => {
  // if column has a rawValue method, use it
  if (column.rawValue && typeof column.rawValue === 'function')
    return column.rawValue(item);

  return item[column.key];
};

const sanitizeFilters = (filters) => {
  if (!filters || typeof filters !== 'object') return {};

  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (Array.isArray(value) && value.length > 0) {
      acc[key] = value.map((item) => String(item));
    }
    return acc;
  }, {});
};

const normalizeFilters = (filters) => {
  if (!filters || typeof filters !== 'object') return {};

  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (Array.isArray(value) && value.length > 0) {
      acc[key] = value.map((item) => String(item)).sort();
    }
    return acc;
  }, {});
};

const areFiltersEqual = (filtersA, filtersB) => {
  const normalizedA = normalizeFilters(filtersA);
  const normalizedB = normalizeFilters(filtersB);

  const keysA = Object.keys(normalizedA);
  const keysB = Object.keys(normalizedB);

  if (keysA.length !== keysB.length) return false;

  return keysA.every((key) => {
    const valuesA = normalizedA[key];
    const valuesB = normalizedB[key];
    if (!valuesB || valuesA.length !== valuesB.length) return false;
    return valuesA.every((value, index) => value === valuesB[index]);
  });
};

// Fonction utilitaire pour convertir les données en CSV
const convertToCSV = (data, columns) => {
  if (!data || data.length === 0) return '';

  // En-têtes du CSV
  const headers = columns.map(col => col.label).join(',');

  // Lignes de données
  const rows = data.map(item => {
    return columns.map(column => {
      // Récupérer la valeur brute pour l'export
      let value = getRawCellValue(item, column);

      // Convertir en chaîne et échapper les virgules et guillemets
      if (value === null || value === undefined) value = '';
      value = String(value);

      // Si la valeur contient une virgule, des guillemets ou un saut de ligne, l'entourer de guillemets
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        // Échapper les guillemets en les doublant
        value = value.replace(/"/g, '""');
        // Entourer de guillemets
        value = `"${value}"`;
      }

      return value;
    }).join(',');
  }).join('\n');

  return `${headers}\n${rows}`;
};

// Fonction pour déclencher le téléchargement
const downloadCSV = (csvContent, filename = 'export.csv') => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
const PAGE_SIZE_OPTIONS = [15, 25, 50, 100, 200];

const SortableTable = ({
  data,
  columns,
  emptyMessage,
  countLabel,
  disableFilters = false,
  initialSortBy = null,
  exportable = false,
  exportFilename = 'export.csv',
  onRowClick = null,
  density = 'comfortable', // 'comfortable' | 'compact'
  wrap = false,
  maxHeight = null,
  minHeight = null,
  initialFilters = null,
  onFiltersChange = null,
  pagination = false,
  defaultPageSize = 50,
  initialPage = 1,
  onPaginationChange = null,
}) => {
  const [columnWidths, setColumnWidths] = useState(() => (
    columns.reduce((acc, column) => {
      if (column.initialWidth) acc[column.key] = column.initialWidth;
      return acc;
    }, {})
  ));
  const resizeRef = useRef(null);

  const [sortConfig, setSortConfig] = useState(
    initialSortBy || {
      key: columns[0].key,
      direction: 'ascending'
    }
  );
  const [filters, setFilters] = useState(() => (initialFilters && typeof initialFilters === 'object'
    ? sanitizeFilters(initialFilters)
    : {}));
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const [currentPage, setCurrentPage] = useState(
    typeof initialPage === 'number' && initialPage >= 1 ? initialPage : 1
  );
  const [pageSize, setPageSize] = useState(
    PAGE_SIZE_OPTIONS.includes(defaultPageSize) ? defaultPageSize : PAGE_SIZE_OPTIONS[1]
  );
  const filterSelectRef = useRef(null);

  const selectStyles = useSelectStyles();
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', onColumnResize);
      document.removeEventListener('mouseup', stopColumnResize);
    };
  }, []);

  useEffect(() => {
    setColumnWidths((prev) => {
      const next = { ...prev };
      columns.forEach((column) => {
        if (!(column.key in next) && column.initialWidth) {
          next[column.key] = column.initialWidth;
        }
      });
      return next;
    });
  }, [columns]);
  
  // Sync internal filters with provided initialFilters when it changes
  useEffect(() => {
    if (initialFilters && typeof initialFilters === 'object') {
      const sanitizedInitial = sanitizeFilters(initialFilters);
      setFilters((prev) => (
        areFiltersEqual(prev, sanitizedInitial)
          ? prev
          : sanitizedInitial
      ));
    } else if (initialFilters === null || initialFilters === undefined) {
      setFilters((prev) => (
        Object.keys(prev).length === 0 ? prev : {}
      ));
    }
  }, [initialFilters]);

  // Notify parent when filters change
  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange(filters);
    }
  }, [filters, onFiltersChange]);

  // Reset to first page when filters or sort changes
  useEffect(() => {
    if (pagination) setCurrentPage(1);
  }, [filters, sortConfig, pagination]);

  // Sync currentPage from external initialPage (e.g. URL param changes via browser nav)
  useEffect(() => {
    if (!pagination) return;
    const normalized = typeof initialPage === 'number' && initialPage >= 1 ? initialPage : 1;
    setCurrentPage((prev) => (prev === normalized ? prev : normalized));
  }, [initialPage, pagination]);

  // Sync pageSize from external defaultPageSize changes
  useEffect(() => {
    if (!pagination) return;
    if (PAGE_SIZE_OPTIONS.includes(defaultPageSize)) {
      setPageSize((prev) => (prev === defaultPageSize ? prev : defaultPageSize));
    }
  }, [defaultPageSize, pagination]);

  // Notify parent when pagination changes
  useEffect(() => {
    if (pagination && onPaginationChange) {
      onPaginationChange(currentPage, pageSize);
    }
  }, [currentPage, pageSize, pagination, onPaginationChange]);
  // Pour fermer le filtre lors d'un clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterSelectRef.current && !filterSelectRef.current.contains(event.target)) {
        setActiveFilterColumn(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Modifié pour générer les options de filtre à partir des données filtrées
  const dynamicColumnOptions = useMemo(() => {
    const options = {};

    columns.forEach(column => {
      if (column.filterable !== false) {
        // Créer un Set pour les valeurs uniques
        const uniqueValues = new Set();

        // Déterminer quelles données utiliser pour ce filtre
        // Filtrer les données en appliquant tous les filtres SAUF celui de la colonne actuelle
        let dataForThisColumn = [...data];

        Object.keys(filters).forEach(key => {
          // Ne pas appliquer le filtre de la colonne en cours
          if (key !== column.key) {
            const filterValues = filters[key];
            if (filterValues && filterValues.length > 0) {
              const filterColumn = columns.find(col => col.key === key);
              dataForThisColumn = dataForThisColumn.filter(item => {
                const itemValue = getRawCellValue(item, filterColumn);
                return filterValues.includes(String(itemValue));
              });
            }
          }
        });

        // Extraire les valeurs uniques à partir des données filtrées
        dataForThisColumn.forEach(item => {
          const value = getRawCellValue(item, column);
          if (value !== undefined && value !== null) {
            uniqueValues.add(String(value));
          }
        });

        // Convertir les valeurs uniques en options pour react-select
        options[column.key] = Array.from(uniqueValues)
          .sort()
          .map(value => ({ value, label: value }));
      }
    });

    return options;
  }, [data, columns, filters]);

  // Handle sorting
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const startColumnResize = (event, columnKey) => {
    event.preventDefault();
    const th = event.target.closest('th');
    const startWidth = columnWidths[columnKey] || th.offsetWidth;
    resizeRef.current = {
      columnKey,
      startX: event.clientX,
      startWidth
    };
    document.addEventListener('mousemove', onColumnResize);
    document.addEventListener('mouseup', stopColumnResize);
  };

  const onColumnResize = (event) => {
    if (!resizeRef.current) return;
    const { columnKey, startX, startWidth } = resizeRef.current;
    const delta = event.clientX - startX;
    const newWidth = Math.max(80, startWidth + delta);
    setColumnWidths((prev) => ({ ...prev, [columnKey]: newWidth }));
  };

  const stopColumnResize = () => {
    resizeRef.current = null;
    document.removeEventListener('mousemove', onColumnResize);
    document.removeEventListener('mouseup', stopColumnResize);
  };

  // Toggle filter dropdown
  const toggleFilter = (columnKey) => {
    setActiveFilterColumn(activeFilterColumn === columnKey ? null : columnKey);
  };

  // Handle filter change
  const handleFilterChange = (selectedOptions, columnKey) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      const newFilters = { ...filters };
      delete newFilters[columnKey];
      setFilters(newFilters);
    } else {
      setFilters({
        ...filters,
        [columnKey]: selectedOptions.map(option => option.value)
      });
    }
  };

  const applyFilterFromCellValue = (column, rawValue) => {
    if (disableFilters || !column || column.filterable === false) return;
    if (rawValue === undefined || rawValue === null) return;

    const value = String(rawValue);

    setFilters((prev) => {
      const existingValues = prev[column.key] || [];
      if (existingValues.includes(value)) {
        return prev;
      }

      return {
        ...prev,
        [column.key]: [...existingValues, value]
      };
    });

    setActiveFilterColumn(null);
  };

  const handleCellValueClick = (event, column, rawValue) => {
    event.stopPropagation();
    applyFilterFromCellValue(column, rawValue);
  };

  const handleCellValueKeyDown = (event, column, rawValue) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      applyFilterFromCellValue(column, rawValue);
    }
  };

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filteredData = [...data];

    // Apply filters
    Object.keys(filters).forEach(key => {
      const filterValues = filters[key];
      if (filterValues && filterValues.length > 0) {
        const column = columns.find(col => col.key === key);

        filteredData = filteredData.filter(item => {
          const itemValue = getRawCellValue(item, column);

          return filterValues.includes(String(itemValue));
        });
      }
    });

    // Sort filtered data
    return filteredData.sort((a, b) => {
      // Find column for custom sorting
      const column = columns.find(col => col.key === sortConfig.key);

      // Use custom sort function if provided
      if (column && column.sortFunction) {
        return column.sortFunction(a, b, sortConfig.direction);
      }

      // Get values to compare - use raw values
      let aValue = column && column.render ?
        getRawCellValue(a, column) :
        a[sortConfig.key];

      let bValue = column && column.render ?
        getRawCellValue(b, column) :
        b[sortConfig.key];

      // Handle undefined or null values
      if (aValue === undefined || aValue === null) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (bValue === undefined || bValue === null) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }

      // Attempt to convert to numbers for numeric comparison (including floats)
      const aNum = Number(aValue);
      const bNum = Number(bValue);

      // If both values can be converted to numbers, use numeric comparison
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortConfig.direction === 'ascending' ? aNum - bNum : bNum - aNum;
      }

      // Otherwise, convert to strings and compare
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();

      // Default string comparison
      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig, columns, filters]);

  const totalPages = pagination ? Math.ceil(filteredAndSortedData.length / pageSize) : 1;

  const paginatedData = useMemo(() => {
    if (!pagination) return filteredAndSortedData;
    const start = (currentPage - 1) * pageSize;
    return filteredAndSortedData.slice(start, start + pageSize);
  }, [filteredAndSortedData, pagination, currentPage, pageSize]);

  // Generate sort indicators
  const getSortDirectionIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return (
        <ChevronsUpDown className="w-3 h-3 ml-1 text-gray-400" strokeWidth={2}/>
      );
    }

    return sortConfig.direction === 'ascending' ? (
      <ChevronUp className="w-3 h-3 ml-1 text-blue-400" strokeWidth={5} />
    ) : (
      <ChevronDown className="w-3 h-3 ml-1 text-blue-400" strokeWidth={5} />
    );
  };

  // Generate filter indicator
  const getFilterIcon = (columnKey) => {
    if (disableFilters) return null;

    const isFiltered = filters[columnKey] && filters[columnKey].length > 0;

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleFilter(columnKey);
        }}
        className={`inline-flex items-center justify-center w-4 h-4 ml-2 ${isFiltered ? 'text-blue-500 dark:text-blue-400 [&_path]:fill-blue-500' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
        style={{ minWidth: '16px', minHeight: '16px' }}
      >
        <Filter className="w-3.5 h-3.5 " />
      </button>
    );
  };

  // Fonction pour exporter les données
  const handleExport = () => {
    const csvContent = convertToCSV(filteredAndSortedData, columns);
    downloadCSV(csvContent, exportFilename);
  };

  const densityClasses = useMemo(() => {
    if (density === 'compact') {
      return {
        header: 'px-3 py-2 text-xs',
        cell: 'px-3 py-1 text-xs',
        footer: 'py-1 text-xs'
      };
    }
    return {
      header: 'px-4 py-3 text-xs',
      cell: 'px-4 py-2 text-sm',
      footer: 'py-2 text-xs'
    };
  }, [density]);

  const cellContentClass = wrap
    ? 'whitespace-pre-wrap break-words w-full'
    : 'whitespace-nowrap overflow-hidden text-ellipsis w-full';

  const containerStyle = useMemo(() => {
    const style = {};

    if (minHeight !== undefined && minHeight !== null) {
      style.minHeight = typeof minHeight === 'number' ? `${minHeight}px` : minHeight;
    }

    if (!maxHeight) {
      return { ...style, height: '100%', maxHeight: 'none' };
    }
    if (typeof maxHeight === 'number') {
      return { ...style, maxHeight: `${maxHeight}px` };
    }
    return { ...style, maxHeight };
  }, [maxHeight, minHeight]);

  return (
    <div className="flex flex-col h-full relative" style={{ width: '100%', ...containerStyle }}>
      <div className="overflow-auto flex-grow scroll-bar">
        {data.length > 0 ? (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 table-fixed">
            <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0 z-10">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={`${densityClasses.header} text-left font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider relative select-none`}
                    style={{
                      width: columnWidths[column.key] ? `${columnWidths[column.key]}px` : undefined,
                      minWidth: columnWidths[column.key] ? `${columnWidths[column.key]}px` : undefined
                    }}
                  >
                    <div className="flex items-center relative">
                      <div
                        onClick={() => requestSort(column.key)}
                        className="flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        <span>{column.label}</span>
                        {getSortDirectionIcon(column.key)}
                      </div>

                      {column.filterable !== false && !disableFilters && (
                        <>
                          {getFilterIcon(column.key)}

                          {activeFilterColumn === column.key && (
                            <div
                              ref={filterSelectRef}
                              className={`absolute ${
                                columns.findIndex(col => col.key === column.key) === 0
                                  ? 'left-0'
                                  : 'right-0'
                              } top-6 mt-1 w-64 z-50 bg-white dark:bg-gray-800 shadow-lg rounded-md border border-gray-200 dark:border-gray-700`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="p-2">
                                <Select
                                  isMulti
                                  closeMenuOnSelect={false}
                                  options={dynamicColumnOptions[column.key] || []}
                                  onChange={(selected) => handleFilterChange(selected, column.key)}
                                  value={(filters[column.key] || []).map(value => ({ value, label: value }))}
                                  placeholder="Filtrer..."
                                  classNamePrefix="react-select"
                                  className="text-sm text-gray-700 dark:text-gray-300"
                                  menuPortalTarget={document.body}
                                  styles={selectStyles}
                                />

                                <div className="flex justify-between mt-2">
                                  <button
                                    onClick={() => handleFilterChange([], column.key)}
                                    className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                                  >
                                    Réinitialiser
                                  </button>
                                  <button
                                    onClick={() => setActiveFilterColumn(null)}
                                    className="text-xs text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                  >
                                    Appliquer
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <span
                      className="absolute top-0 right-0 h-full w-1 cursor-col-resize bg-transparent"
                      onMouseDown={(event) => startColumnResize(event, column.key)}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedData.map((item, index) => {
                if (item.rawValue && typeof item.rawValue === 'function')
                  item.rawValue = item.rawValue(item);

                return <tr key={index} className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${onRowClick ? 'cursor-pointer' : ''}`} onClick={() => onRowClick && onRowClick(item)}>
                  {columns.map((column) => {
                    const allowOverflow = column.allowOverflow;
                    const displayValue = column.render ? column.render(item) : item[column.key];
                    const rawValue = getRawCellValue(item, column);
                    const isCellFilterable = !disableFilters && column.filterable !== false && rawValue !== undefined && rawValue !== null;
                    const baseCellClass = allowOverflow
                      ? (wrap ? 'whitespace-pre-wrap break-words w-full' : 'whitespace-nowrap w-full')
                      : cellContentClass;
                    const interactiveCellClasses = isCellFilterable ? ' cursor-pointer hover:underline focus:outline-none focus:ring-1 focus:ring-blue-400' : '';

                    return (
                      <td
                        key={`${index}-${column.key}`}
                        className={`${densityClasses.cell} text-gray-700 dark:text-gray-300 text-left align-top`}
                        style={{
                          width: columnWidths[column.key] ? `${columnWidths[column.key]}px` : undefined,
                          minWidth: columnWidths[column.key] ? `${columnWidths[column.key]}px` : undefined
                        }}
                      >
                        <div
                          className={`${baseCellClass}${interactiveCellClasses}`}
                          style={{
                            maxWidth: columnWidths[column.key]
                              ? `${Math.max(columnWidths[column.key] - 16, 60)}px`
                              : '100%',
                            overflow: allowOverflow ? 'visible' : undefined
                          }}
                          title={wrap || allowOverflow ? undefined : (rawValue !== undefined && rawValue !== null ? String(rawValue) : '')}
                          role={isCellFilterable ? 'button' : undefined}
                          tabIndex={isCellFilterable ? 0 : undefined}
                          onClick={(event) => isCellFilterable && handleCellValueClick(event, column, rawValue)}
                          onKeyDown={(event) => isCellFilterable && handleCellValueKeyDown(event, column, rawValue)}
                        >
                          {displayValue}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              })}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-10 text-gray-500 dark:text-gray-400 flex-grow flex items-center justify-center">
            <div>
              <CheckCircle2 className="mx-auto h-12 w-12 text-gray-400 mb-3" aria-hidden="true" />
              <p className="text-left">{emptyMessage || "Aucune donnée disponible"}</p>
            </div>
          </div>
        )}
      </div>

      {/* Barre d'informations fixe en bas */}
      {data.length > 0 && (
        <div className={`sticky bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 text-xs text-gray-500 dark:text-gray-400 flex justify-between items-center ${densityClasses.footer}`}>
          <div className="flex items-center gap-2">
            <p>{countLabel || `Total: ${filteredAndSortedData.length} éléments`}</p>

            {exportable && filteredAndSortedData.length > 0 && (
              <button
                onClick={handleExport}
                className="ml-3 text-xs text-green-600 hover:text-green-800 dark:text-green-500 dark:hover:text-green-400 flex items-center"
                title="Exporter en CSV"
              >
                <Download className="h-4 w-4 mr-1" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {!disableFilters && Object.keys(filters).length > 0 && (
              <button
                onClick={() => setFilters({})}
                className="text-xs text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center"
              >
                <X className="w-3 h-3 mr-1" />
                Effacer tous les filtres
              </button>
            )}

            {pagination && totalPages > 0 && (
              <div className="flex items-center gap-2">
                <label className="text-gray-400 dark:text-gray-500">Lignes:</label>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                  className="bg-transparent border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 text-xs text-gray-600 dark:text-gray-300 focus:outline-none"
                >
                  {PAGE_SIZE_OPTIONS.map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>

                <span className="text-gray-400 dark:text-gray-500">
                  {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filteredAndSortedData.length)} of {filteredAndSortedData.length}
                </span>

                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                  className="px-1 disabled:opacity-30 hover:text-gray-700 dark:hover:text-gray-200"
                  title="First page"
                >
                  «
                </button>
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                  className="px-1 disabled:opacity-30 hover:text-gray-700 dark:hover:text-gray-200"
                  title="Previous page"
                >
                  ‹
                </button>
                <span className="px-1">{currentPage} / {totalPages}</span>
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                  className="px-1 disabled:opacity-30 hover:text-gray-700 dark:hover:text-gray-200"
                  title="Next page"
                >
                  ›
                </button>
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                  className="px-1 disabled:opacity-30 hover:text-gray-700 dark:hover:text-gray-200"
                  title="Last page"
                >
                  »
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {data.length > 0 && filteredAndSortedData.length === 0 && (
        <div className="sticky bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 py-2 px-4 text-sm text-gray-500 dark:text-gray-400 text-center">
          <p>Aucun résultat ne correspond aux filtres appliqués</p>
          <button
            onClick={() => setFilters({})}
            className="mt-2 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Réinitialiser les filtres
          </button>
        </div>
      )}
    </div>
  );
};

export default SortableTable;
